// packages/core/src/learning/schools/hard-money.ts
// ─────────────────────────────────────────────────────────────────────────
// Authored exhibit for "Hard money". Matches boglehead-indexing.ts gold
// standard.
//
// ID-bound fields (the validator + compiler guard these):
//   - figures:        FigureId[]  — empty in source AND kept []. The worldview
//                     names no figures. Canon authors (Ludwig von Mises,
//                     Friedrich Hayek, Murray Rothbard) are NOT figures-tally
//                     entries per the protocol, and none currently exist as a
//                     FigureId in figureRegistry.ts. This school contributes
//                     nothing to the running figure tally.
//   - applyActions:   ApplyAction union — type-checked at compile time. Only
//                     live/allowed action types emitted.
//   - featuredLessonIds / debateIds — [] until the cross-ref pass.
// ─────────────────────────────────────────────────────────────────────────

import type { SchoolExhibitEntry } from '../SchoolExhibitMeta';

export const HARD_MONEY_EXHIBIT: SchoolExhibitEntry = {
	id: 'hard-money',
	tagline: 'Hard money believes money should be hard to dilute.',
	worldviewSummary:
		'Hard money is paying attention to trust, scarcity, purchasing power, and the danger of money being created too easily. This lens protects you from ignoring inflation, currency weakness, debt cycles, and the quiet loss of buying power. It accepts monetary discipline, skepticism, and slower flexibility because it believes loose money can hide real costs. The blind spot is that hard money can become too rigid, too suspicious of every institution, or too focused on scarcity while underweighting growth, employment, and crisis response. This lens is useful when it teaches people to ask what their money will actually buy over time, not when it turns into doom thinking.',
	coreBeliefs: [
		'Hard money believes money should be difficult to create on demand.',
		'Hard money believes inflation is not just a statistic. It changes what savings can buy.',
		'Hard money believes debt fueled systems can look stable until trust weakens.',
		'Hard money believes monetary discipline protects savers from hidden dilution.',
		'Hard money believes scarcity matters because unlimited issuance can change incentives.',
		'Hard money believes people should think in purchasing power, not just account balances.',
	],
	warnsAgainst: [
		'Assuming cash keeps the same value just because the number stays the same.',
		'Trusting debt fueled growth without asking who absorbs the cost later.',
		'Thinking money creation has no tradeoff for workers, savers, renters, and future buyers.',
		'Turning every economic problem into a simple currency collapse story.',
	],
	helps: 'This lens helps people who want to understand inflation, purchasing power, debt cycles, monetary trust, and why some investors care deeply about scarce money. It may not fit when someone needs practical flexibility, income growth, or a more balanced view of how governments respond to recessions and crises.',
	figures: [],
	canon: [
		{
			title: 'The Theory of Money and Credit',
			author: 'Ludwig von Mises',
			oneLineIdea:
				'Money is not neutral in everyday life because changes in credit and money supply affect prices, incentives, and behavior.',
		},
		{
			title: 'Denationalisation of Money',
			author: 'Friedrich Hayek',
			oneLineIdea:
				'Competition and discipline in money can be seen as a check against political misuse.',
		},
		{
			title: 'What Has Government Done to Our Money?',
			author: 'Murray Rothbard',
			oneLineIdea:
				'The hard money tradition argues that state controlled money can weaken purchasing power when issuance is abused.',
		},
		{
			title: 'Gold standard tradition',
			oneLineIdea:
				'A scarce monetary base can limit discretion, but it can also reduce flexibility during shocks.',
		},
		{
			title: 'Purchasing power lens',
			oneLineIdea:
				'The number in an account matters less than what that number can actually buy.',
		},
	],
	featuredLessonIds: [],
	nativeModuleId: 'school-hard-money',
	debateIds: [],
	applyActions: [
		{
			label: 'Check whether your current picture depends on prices staying calm',
			action: {
				type: 'openDashboard',
				label: 'Review your current picture',
			},
		},
		{
			label: 'Test how higher costs change your freedom path',
			action: {
				type: 'openSimulator',
				label: 'Simulate higher expenses',
				scenarioType: 'custom',
			},
		},
		{
			label: 'Check how long your runway lasts if costs rise',
			action: {
				type: 'calculateRunway',
				label: 'Check your runway',
			},
		},
		{
			label: 'Write your purchasing power concern in plain language',
			action: {
				type: 'noToolAction',
				label: 'Name the concern',
				prompt: 'Write what would happen to your plan if prices rose faster than your income for one year.',
			},
		},
	],
	marketingHooks: [
		{
			line: 'Hard money asks one brutal question: what will your savings actually buy later?',
			kind: 'hook',
		},
		{
			line: 'The hidden cost of loose money is paid in purchasing power.',
			kind: 'hook',
		},
		{
			line: 'When did inflation change a decision you thought was already affordable?',
			kind: 'interview-question',
		},
		{
			line: 'Where does hard money thinking protect people, and where does it become too rigid?',
			kind: 'interview-question',
		},
		{
			line: 'Nisk helps people test purchasing power, inflation pressure, and money worldviews without turning them into fear.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'This school teaches why some people distrust money that can be created too easily.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Show how the same dollar amount can feel different when rent, food, gas, and insurance rise.',
			kind: 'creator-prompt',
		},
		{
			line: 'Ask someone whether they care more about flexibility or monetary discipline, then make them explain the tradeoff.',
			kind: 'creator-prompt',
		},
	],
	revision: 1,
};
